import type {
  AuthUser,
  CitySuggestion,
  EventListStatus,
  EventListType,
  EventRecord,
  EventSummary,
} from '../lib/types';

const BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? '/api';

class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

async function request<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...((init.headers as Record<string, string>) ?? {}),
  };
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers,
    credentials: 'include',
  });
  const text = await res.text();
  const body = text ? safeJson(text) : null;
  if (!res.ok) {
    const errMsg =
      body && typeof body === 'object' && 'error' in body
        ? String((body as { error?: unknown }).error)
        : `HTTP ${res.status}`;
    throw new ApiError(errMsg, res.status);
  }
  return body as T;
}

function safeJson(s: string): unknown {
  try {
    return JSON.parse(s);
  } catch {
    return null;
  }
}

export const api = {
  async register(email: string, password: string) {
    return request<{ message: string }>('/auth/register', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  },
  async login(email: string, password: string) {
    return request<{ user: AuthUser }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  },
  async logout() {
    return request<void>('/auth/logout', { method: 'POST' });
  },
  async verifyEmail(token: string) {
    return request<void>('/auth/verify', {
      method: 'POST',
      body: JSON.stringify({ token }),
    });
  },
  async resendVerification(email: string) {
    return request<void>('/auth/resend-verification', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  },
  async requestPasswordReset(email: string) {
    return request<void>('/auth/request-password-reset', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  },
  async resetPassword(token: string, newPassword: string) {
    return request<void>('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, new_password: newPassword }),
    });
  },
  async me() {
    return request<AuthUser>('/me');
  },
  async listEvents(
    status: EventListStatus = 'active',
    type: EventListType = 'all',
  ) {
    const params = new URLSearchParams();
    if (status !== 'active') params.set('status', status);
    if (type !== 'all') params.set('type', type);
    const q = params.toString() ? `?${params}` : '';
    return request<EventSummary[]>(`/events${q}`);
  },
  async createEvent(input: Omit<EventRecord, 'id'>) {
    return request<EventRecord>('/events', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },
  async getEvent(id: string) {
    return request<EventRecord>(`/events/${id}`);
  },
  async updateEvent(id: string, input: Omit<EventRecord, 'id'>) {
    return request<EventRecord>(`/events/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    });
  },
  async copyEvent(id: string, name: string) {
    return request<EventRecord>(`/events/${id}/copy`, {
      method: 'POST',
      body: JSON.stringify({ name }),
    });
  },
  async archiveEvent(id: string) {
    return request<EventRecord>(`/events/${id}/archive`, { method: 'POST' });
  },
  async unarchiveEvent(id: string) {
    return request<EventRecord>(`/events/${id}/unarchive`, { method: 'POST' });
  },
  async deleteEvent(id: string) {
    return request<void>(`/events/${id}`, { method: 'DELETE' });
  },
  async searchCities(query: string, country?: string, signal?: AbortSignal) {
    const p = new URLSearchParams({ q: query });
    if (country) p.set('country', country);
    return request<CitySuggestion[]>(`/geo/cities?${p.toString()}`, { signal });
  },
};

export { ApiError };
